import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LogIn, GraduationCap, X, ChevronUp, MessageCircle } from "lucide-react";
import { CONTACT } from "../data/mock";

const HIDDEN_PATH_PREFIXES = [
  "/area-soci",
  "/dashboard",
  "/admin",
  "/checkout",
  "/verifica-attestato",
  "/reset-password",
  "/onboarding",
];

const STORAGE_KEY = "acdt_sticky_cta_minimized";

/**
 * Sticky CTA flottante bottom-right: pulsanti "Accesso Soci" (blu) + "Iscriviti al corso" (oro).
 * Appare dopo 400px di scroll, solo su pagine pubbliche (nascosto in area soci/admin/checkout).
 * Minimizzabile via X (persistente in localStorage per la sessione).
 */
export default function StickyCTA() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const path = location.pathname;
  const isHidden = HIDDEN_PATH_PREFIXES.some((p) => path.startsWith(p));
  if (isHidden || !visible) return null;

  const handleMinimize = () => {
    setMinimized(true);
    try { sessionStorage.setItem(STORAGE_KEY, "1"); } catch { /* ignore */ }
  };
  const handleExpand = () => {
    setMinimized(false);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const trackClick = (dest) => {
    try {
      window.posthog?.capture?.("sticky_cta_click", { destination: dest, path });
    } catch { /* posthog non disponibile */ }
  };

  // Stato minimizzato: piccolo pallino
  if (minimized) {
    return (
      <>
        <button
          type="button"
          onClick={handleExpand}
          aria-label="Riapri accesso rapido"
          data-testid="sticky-cta-expand"
          className="fixed z-40 bottom-24 right-5 h-12 w-12 rounded-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white shadow-2xl flex items-center justify-center transition-transform hover:scale-110"
        >
          <LogIn className="h-5 w-5" />
        </button>
        <WhatsAppFab onClick={() => trackClick("whatsapp")} />
      </>
    );
  }

  return (
    <>
      <div
        className="fixed z-40 bottom-24 right-5 flex flex-col items-end gap-2 animate-[stickyIn_0.4s_cubic-bezier(0.34,1.56,0.64,1)]"
        data-testid="sticky-cta"
      >
      <style>{`
        @keyframes stickyIn {
          from { opacity: 0; transform: translateY(20px) scale(0.9); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes stickyPulse {
          0%, 100% { box-shadow: 0 10px 30px -8px rgba(30,64,175,0.5), 0 0 0 0 rgba(30,64,175,0.4); }
          50% { box-shadow: 0 10px 30px -8px rgba(30,64,175,0.5), 0 0 0 12px rgba(30,64,175,0); }
        }
        .sticky-pulse { animation: stickyPulse 2.4s ease-in-out infinite; }
      `}</style>

      {/* Close */}
      <button
        type="button"
        onClick={handleMinimize}
        aria-label="Minimizza"
        data-testid="sticky-cta-close"
        className="h-7 w-7 rounded-full bg-white/95 hover:bg-white text-slate-500 hover:text-slate-800 shadow-md flex items-center justify-center transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>

      {/* CTA Corso */}
      <a
        href="/#pacchetti"
        onClick={() => trackClick("iscriviti_corso")}
        data-testid="sticky-cta-corso"
        className="group inline-flex items-center gap-2 pl-4 pr-5 h-12 rounded-full bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold text-[13.5px] shadow-xl transition-all hover:scale-105 hover:-translate-y-0.5"
      >
        <GraduationCap className="h-4 w-4" />
        <span>Iscriviti al corso</span>
      </a>

      {/* CTA Accesso Soci (principale, con pulse) */}
      <Link
        to="/area-soci"
        onClick={() => trackClick("accesso_soci")}
        data-testid="sticky-cta-accesso-soci"
        className="sticky-pulse group inline-flex items-center gap-2 pl-4 pr-5 h-13 py-3 rounded-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-extrabold text-[14px] transition-all hover:scale-105 hover:-translate-y-0.5"
      >
        <LogIn className="h-4 w-4" />
        <span>Accesso Soci</span>
        <ChevronUp className="h-3.5 w-3.5 rotate-90 opacity-70 group-hover:translate-x-0.5 transition-transform" />
      </Link>
      </div>

      {/* WhatsApp FAB — separato a sinistra */}
      <WhatsAppFab onClick={() => trackClick("whatsapp")} />
    </>
  );
}

function WhatsAppFab({ onClick }) {
  return (
    <a
      href={CONTACT.whatsappDirect}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      aria-label="Contattaci su WhatsApp"
      data-testid="sticky-cta-whatsapp"
      className="fixed z-40 bottom-5 left-5 h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white flex items-center justify-center shadow-xl transition-all hover:scale-110 wa-pulse"
    >
      <MessageCircle className="h-6 w-6" />
    </a>
  );
}
